import React from 'react';
import Wizard from 'components/Wizard/Wizard';
import { StepType } from 'components/Wizard/Wizard';
import ResearchAreaList from 'components/CreateAccountForm/ResearchAreaList';
import { TextInput, CheckboxInput } from 'components/CreateAccountForm/Inputs';
import { VALIDATION_SCHEMA } from 'components/App/CreateAccount/AppLayout';
import { joinTypes } from 'api/account';
import { usePrivacyPolicyURLContext, useTermsofUseURLContext } from 'utilities/contexts';

// wizard for CreateAccount form with react-hook-form
export default function CreateAccountWizard({
    accountCreateSettings, errors, register, setValue, getValues, trigger, onSubmit, onCompleteContactInfo,
    checkForDuplicateUsername, isPediatric, isThirdPartyUserName, firstName, fieldIsDirty, isSubmitting, joinType,
    disableAnimation = false
}) {

    const privacyPolicyURL = usePrivacyPolicyURLContext();
    const termsofUseURL = useTermsofUseURLContext();

    // determine if all fields in a step are valid to continue
    // callback is for wiring with Wizard onNextStep (see Wizard.js)
    // stepDependencies is a list of field names that need to be validated to continue past this step
    // extraValidation is a function that contains any additional validation logic outside VALIDATION_SCHEMA, returns a boolean 
    const onStep = (callback, stepDependencies, extraValidation) => {
        trigger(stepDependencies).then(triggerResult => {
            if (extraValidation)
                callback(triggerResult && extraValidation());
            else
                callback(triggerResult);
        });
    }

    const stepHasServerErrors = (stepFields) => {
        if (!errors || !stepFields)
            return false;

        let stepErrorCount = Object.keys(errors).reduce((accumulator, errorField) => {         
            if (stepFields.indexOf(errorField) !== -1 && errors[errorField].type === 'server')            
                accumulator++;
            
            return accumulator;
        }, 0);

        if (stepErrorCount === 0)
            return false;

        return true;
    }

    /* begin step prep */

    // When JoinFromGuid, prevent dateOfBirth from changing adult/pediatric
    const dateOfBirthOptions = { ...VALIDATION_SCHEMA.dateOfBirth }
    if (joinType === joinTypes.JoinFromGuid) {
        dateOfBirthOptions.validate = {
            ...VALIDATION_SCHEMA.dateOfBirth.validate,
            validAgeGroup: (v, formValues) => {
                if (formValues.day && formValues.month && formValues.year) {
                    const dobSecs = Date.parse(`${formValues.year.padStart(4, "0")}-${formValues.month.padStart(2, "0")}-${formValues.day.padStart(2, "0")}`);
                    if (dobSecs > accountCreateSettings.msecOf18Years && !formValues.isPediatric) {
                        return "Date of Birth must be 18 years or older."
                    } else if (dobSecs < accountCreateSettings.msecOf18Years && formValues.isPediatric) {
                        return "Date of Birth must be under 18 years."
                    }
                }
                return true;
            }
        }
    }

    const basicDetailsStep = (
        <>
            <div className="align-with-inputs">
                <h2>Please enter basic details about yourself or your child that you wish to enroll.</h2>
                <hr className="my-5" />
            </div>
            
            <TextInput name="firstName" label="First Name" isDirty={fieldIsDirty('firstName')}
                options={VALIDATION_SCHEMA.firstName} errors={errors} register={register} />
            <TextInput name="lastName" label="Last Name" isDirty={fieldIsDirty('lastName')}
                options={VALIDATION_SCHEMA.lastName} errors={errors} register={register} />
            <TextInput name="dateOfBirth" type="hidden" label="Date of Birth"
                options={dateOfBirthOptions} errors={errors} register={register} />
            <div className="packed-input-row">  
                <TextInput name="month" type="number" placeholder="MM" label="Birth Month" hideLabel={true} isDirty={fieldIsDirty('month')}
                    options={VALIDATION_SCHEMA.month} errors={errors} register={register} />
                <TextInput name="day" type="number" placeholder="DD" label="Birth Day" hideLabel={true} isDirty={fieldIsDirty('day')}
                    options={VALIDATION_SCHEMA.day} errors={errors} register={register} />
                <TextInput name="year" type="number" placeholder="YYYY" label="Birth Year" hideLabel={true} isDirty={fieldIsDirty('year')}
                    options={VALIDATION_SCHEMA.year} errors={errors} register={register} />
            </div>
            {/* <CheckboxInput name="pronounGuids" label="Preferred pronouns" values={accountCreateSettings.pronouns}
                options={VALIDATION_SCHEMA.pronounGuids} errors={errors} register={register} isDirty={fieldIsDirty('pronounGuids')} /> */}
        </>
    );

    // dateOfBirth included for server errors
    const basicDetailsFields = ['firstName', 'lastName', 'month', 'day', 'year', 'pronounGuids', 'dateOfBirth'];
    const basicDetailsHasServerErrors = stepHasServerErrors(basicDetailsFields);

    const contactInfoStep = (
        <>
            <div className="align-with-inputs">
                <h2>{isPediatric ? "Please enter basic details about you the parent or guardian." : "Please enter your contact information."}</h2>
                <hr className="my-5" />
            </div>

            {isPediatric &&
                <>
                    <TextInput name="parentFirstName" label="What is your First Name" isDirty={fieldIsDirty('parentFirstName')}
                        options={VALIDATION_SCHEMA.parentFirstName} errors={errors} register={register} />
                    <TextInput name="parentLastName" label="What is your Last Name" isDirty={fieldIsDirty('parentLastName')}
                        options={VALIDATION_SCHEMA.parentLastName} errors={errors} register={register} />
                </>
            }

            <TextInput name="emailAddress" type="email" label={isPediatric ? "What is your Email Address" : "Email Address"}
                options={VALIDATION_SCHEMA.emailAddress} errors={errors} register={register} isDirty={fieldIsDirty('emailAddress')} />

            <TextInput name="phoneNumber" label={isPediatric ? "What is your Phone Number" : "Phone Number"}
                options={VALIDATION_SCHEMA.phoneNumber} errors={errors} register={register} isDirty={fieldIsDirty('phoneNumber')} />
            <CheckboxInput name="phoneNumberTypeGuid" label="Phone number type" hideLabel={true}
                values={accountCreateSettings.phoneNumberTypes} type="radio" isDirty={fieldIsDirty('phoneNumberTypeGuid')}
                options={VALIDATION_SCHEMA.phoneNumberTypeGuid} errors={errors} register={register} />

            <TextInput name="zipCode" label={isPediatric ? "What is your Zip Code" : "Zip Code"}
                options={VALIDATION_SCHEMA.zipCode} errors={errors} register={register} isDirty={fieldIsDirty('zipCode')} />

            {/* {isPediatric &&
                <CheckboxInput name="parentPronounGuids" label="What are your preferred pronouns" values={accountCreateSettings.pronouns}
                options={VALIDATION_SCHEMA.parentPronounGuids} errors={errors} register={register} isDirty={fieldIsDirty('parentPronounGuids')} />
            } */}
        </>
    );
    const contactInfoFields = ['parentFirstName', 'parentLastName', 'phoneNumberTypeGuid', 'phoneNumber', 'emailAddress', 'zipCode', 'parentPronounGuids'];
    const contactInfoHasServerErrors = stepHasServerErrors(contactInfoFields);

    const usernameOptions = {
        ...VALIDATION_SCHEMA.userName,
        validate: async (v) => {
            return await checkForDuplicateUsername(v) || 'An account using this Username already exists. Please pick a different Username.';
        }
    }
    const accountDetailsStep = (
        <>
            <div className="align-with-inputs">
                <h2>
                    {isPediatric ?
                        `Please set ${firstName}'s username${(isThirdPartyUserName) ? "" : " and password"}.`
                        :
                        `Please set your username${(isThirdPartyUserName) ? "" : " and password"}.`
                    }
                </h2>
                <hr className="my-5" />
            </div>
            <TextInput name="userName" label="Username" readonly={isThirdPartyUserName}
                options={usernameOptions} errors={errors} register={register} isDirty={fieldIsDirty('userName')} />
            {!isThirdPartyUserName &&
                <>
                <TextInput name="password" label="Password" type="password" isDirty={fieldIsDirty('password')}
                    options={VALIDATION_SCHEMA.password} errors={errors} register={register} showAllErrors={true} />
                    <TextInput name="confirmPassword" label="Confirm Your Password" type="password" isDirty={fieldIsDirty('confirmPassword')}
                        options={VALIDATION_SCHEMA.confirmPassword} errors={errors} register={register} />
                </>
            }
        </>
    );
    const accountDetailsFields = ['userName', 'password', 'confirmPassword'];
    const accountDetailsHasServerErrors = stepHasServerErrors(accountDetailsFields);

    const personalDetailsStep = (
        <> 
            <div className="align-with-inputs">
                <h2>
                    {isPediatric ? `Please enter ${firstName}'s sex, race, and ethnicity.` : "Please enter your sex, race, and ethnicity."}
                </h2>
                <hr className="my-5" />
            </div>
            <CheckboxInput name="genderGuid" label="Sex"
                values={accountCreateSettings.genders} options={VALIDATION_SCHEMA.genderGuid} type="radio"
                errors={errors} register={register} isDirty={fieldIsDirty('genderGuid')} />
            {/* <CheckboxInput name="genderIdentityGuid" label={`What is your ${isPediatric ? "child's " : ""}current gender identity?`}
                values={accountCreateSettings.genderIdentities} options={VALIDATION_SCHEMA.genderIdentityGuid} type="radio"
                errors={errors} register={register} isDirty={fieldIsDirty('genderIdentityGuid')} /> */}
            <CheckboxInput name="raceGuids" label={`${isPediatric ? "What is your child's " : ""}Race (check all that apply)`}
                values={accountCreateSettings.races} options={VALIDATION_SCHEMA.raceGuids}
                errors={errors} register={register} isDirty={fieldIsDirty('raceGuids')} />
            <CheckboxInput name="ethnicityGuid" label={`${isPediatric ? "What is your child's " : ""}Ethnicity`}
                values={accountCreateSettings.ethnicities} options={VALIDATION_SCHEMA.ethnicityGuid} type="radio"
                errors={errors} register={register} isDirty={fieldIsDirty('ethnicityGuid')} />
        </>
    );
    const personalDetailsFields = ['genderGuid', 'genderIdentityGuid', 'raceGuids', 'ethnicityGuid'];
    const personalDetailsHasServerErrors = stepHasServerErrors(personalDetailsFields);

    const preferencesStep = (
        <div className="align-with-inputs">
            <h2>Your Study Interests</h2>
            <div>
                Sharing your study interests will let us notify you when relevant studies become available. Check boxes for all studies you are interested in hearing about. These can be changed at any time by accessing your profile.
            </div>


            <hr className="my-4" />
            <h3>Research Areas & Conditions</h3>
            <div className="text-center mt-3 mb-4">Click <i className="far fa-plus-square"></i><span className="visually-hidden">button collapsed</span> next to a category to expand its results</div>

            <ResearchAreaList isPediatric={isPediatric} accountCreateSettings={accountCreateSettings} setValue={setValue} getValues={getValues} isAgeLocked={joinType === joinTypes.JoinFromGuid ? true : false} ></ResearchAreaList>

            <hr className="my-4" />

            <div className="terms-of-service">
                Your personal information is kept confidential, and participation in research is always voluntary.
                <br /> <br />
                By clicking CREATE ACCOUNT, you agree to register for the Pitt+Me&reg; community. You also agree to and are protected by the <a href={`${termsofUseURL}`} target="_blank" rel="noreferrer">Terms of Use</a> and <a href={`${privacyPolicyURL}`} target="_blank" rel="noreferrer">Privacy Policy</a> and attest that you are at least 18 years of age and reside in the United States.
            </div>

            <hr className="my-4" />
        </div>);

    /* end step prep */

    return (
        <Wizard finishText="CREATE ACCOUNT" handleFinish={onSubmit} disableFinish={isSubmitting} disableAnimation={disableAnimation} steps={[
            {
                name: "Basic Details", type: StepType.Step, content: basicDetailsStep,
                onNextStep: (callback) => onStep(callback, basicDetailsFields),
                hasErrors: basicDetailsHasServerErrors
            },
            {
                name: "Contact Info", type: StepType.PartialStep, content: contactInfoStep,
                onNextStep: (callback) => onStep(callback, contactInfoFields, onCompleteContactInfo),
                hasErrors: contactInfoHasServerErrors
            },
            {
                name: "Account Details", type: StepType.Step, content: accountDetailsStep,
                onNextStep: (callback) => onStep(callback, accountDetailsFields),
                hasErrors: accountDetailsHasServerErrors
            },
            {
                name: "Personal Details", type: StepType.Step, content: personalDetailsStep,
                onNextStep: (callback) => onStep(callback, personalDetailsFields),
                hasErrors: personalDetailsHasServerErrors
            },
            { name: "Study Interests", type: StepType.Step, content: preferencesStep }
        ]} />
    );
}